import { useEffect, useRef } from 'react';
import type Uppy from '@uppy/core';
import type { UniversalMediaUploaderEvent, UppyMeta, UppyBody } from '../../../components/machines/universal-media-uploader-types';
import type { MultiFileEntry } from '../../../components/machines/multi-file-uploader-types';
import type { MediaType } from '../media-type-config';

interface UseMediaUploaderSyncParams {
  editing: boolean;
  uppyInstance: Uppy<UppyMeta, UppyBody> | null | undefined;
  sendEvent: (event: UniversalMediaUploaderEvent) => void;
  files: Map<string, MultiFileEntry>;
  isMultiFileEnabled: boolean;
  remainingSlots: number;
  allowedFileTypes: string[];
  maxFileSize: number | undefined;
  mediaType: MediaType | undefined;
  onAdd: (
    metadata: Record<string, unknown>,
    uploadedFile: { access_key: string; name?: string },
  ) => void;
}

/**
 * Bridges the universal media uploader machine with the field value model.
 *
 * Handles three concerns that belong together:
 * - Syncing Uppy's maxNumberOfFiles restriction when remainingSlots changes mid-session.
 * - Sending RESET and clearing processed-upload tracking when editing is discarded.
 * - Calling onAdd for each newly completed upload to commit it to the field value.
 */
export default function useMediaUploaderSync({
  editing,
  uppyInstance,
  sendEvent,
  files,
  isMultiFileEnabled,
  remainingSlots,
  allowedFileTypes,
  maxFileSize,
  mediaType,
  onAdd,
}: UseMediaUploaderSyncParams): void {
  const processedUploadsRef = useRef<Set<string>>(new Set());
  const prevEditingRef = useRef(editing);

  // Sync Uppy's maxNumberOfFiles restriction when remainingSlots changes mid-session.
  // Full restrictions object is passed to avoid Uppy's shallow-merge dropping
  // allowedFileTypes and maxFileSize.
  const prevRemainingSlotsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isMultiFileEnabled) return;
    if (!uppyInstance) return;

    const next = remainingSlots;

    // Skip on initial mount — restrictions were already set by initialize().
    if (prevRemainingSlotsRef.current === null) {
      prevRemainingSlotsRef.current = next;
      return;
    }
    if (prevRemainingSlotsRef.current === next) return;

    prevRemainingSlotsRef.current = next;
    uppyInstance.setOptions({
      restrictions: {
        allowedFileTypes,
        maxFileSize,
        maxNumberOfFiles: next,
      },
    });
  }, [isMultiFileEnabled, remainingSlots, allowedFileTypes, maxFileSize, uppyInstance]);

  // Reset uploader state when editing is discarded (editing: true → false).
  // This clears completed/in-progress/error file entries so they don't
  // reappear if the user enters edit mode again.
  useEffect(() => {
    if (prevEditingRef.current && !editing && isMultiFileEnabled) {
      sendEvent({ type: 'RESET' });
      processedUploadsRef.current.clear();
    }
    prevEditingRef.current = editing;
  }, [editing, sendEvent, isMultiFileEnabled]);

  useEffect(() => {
    if (!isMultiFileEnabled) return;
    // Skip when not editing — the RESET effect clears processedUploadsRef
    // in the same effect batch, and the state machine hasn't processed
    // RESET yet so files still has entries. Without this guard we'd
    // re-add every completed upload back into the value on discard.
    if (!editing) return;

    files.forEach((entry, fileId) => {
      if (entry.attachmentData && !processedUploadsRef.current.has(fileId)) {
        processedUploadsRef.current.add(fileId);

        const accessKey = entry.attachmentData.id;

        if (accessKey) {
          onAdd(
            { id: accessKey, size: entry.attachmentData.size, fileName: entry.attachmentData.name },
            {
              access_key: accessKey,
              // Only pass name for attachment types — AttachmentValue.addItem uses it as file name.
              // MediaValue.addItem treats the second arg as caption, so passing the file name
              // would incorrectly populate the caption field for photo/video/audio/signature.
              name: mediaType === 'attachment' ? entry.attachmentData.name : undefined,
            },
          );
        }
      }
    });
  }, [editing, files, onAdd, isMultiFileEnabled, mediaType]);
}
